import { DEFAULT_PARTNER_MARGIN } from '@/constants/locales/calculator';
import { MAX_BENCHMARKING_ITEMS } from '@/constants/scenarios';
import { InvoicingConcept, ScenarioType } from '@/types/scenario';
import { create } from 'zustand';

// ===== Types =====

export type CommonProps = {
  vendorIdList: string[];
  invoicingAmount: number | undefined;
  partnerMargin: number;
  scenario: ScenarioType | undefined;
};

export type BenchmarkingItem = {
  id: string;
  customerId: string | undefined;
  vendorId: string | undefined;
  localCurrency: string;
  invoicingConcept: InvoicingConcept | undefined;
};

// Si querés reconstruir el shape original (common + scenario)
export type BenchmarkingMerged = CommonProps & Omit<BenchmarkingItem, 'id'> & { id: string };

// ===== Helpers =====

const newId = () => crypto.randomUUID();

const defaultCommonProps: CommonProps = {
  vendorIdList: [],
  invoicingAmount: undefined,
  partnerMargin: DEFAULT_PARTNER_MARGIN,
  scenario: 'localSubsidiary',
};

const createEmptyBenchmarking = (overrides?: Partial<Omit<BenchmarkingItem, 'id'>>): BenchmarkingItem => ({
  id: newId(),
  customerId: undefined,
  vendorId: undefined,
  invoicingConcept: undefined,
  localCurrency: 'USD',
  ...overrides,
});

// ===== Store =====

type BenchmarkingStore = {
  // data
  commonProps: CommonProps;
  items: BenchmarkingItem[];

  // common actions
  setCommonProps: (patch: Partial<CommonProps>) => void;
  resetCommonProps: () => void;

  // scenario actions (by id)
  addBenchmarking: (item?: Partial<Omit<BenchmarkingItem, 'id'>>) => string; // devuelve id
  updateBenchmarking: (id: string, patch: Partial<Omit<BenchmarkingItem, 'id'>>) => void;
  removeBenchmarking: (id: string) => void;

  duplicateBenchmarking: (id: string) => string | null; // devuelve nuevo id
  reorderBenchmarking: (fromId: string, toId: string) => void;

  // bulk
  setAllBenchmarking: (items: BenchmarkingItem[]) => void;
  resetBenchmarking: () => void;

  // selectors/helpers
  getBenchmarkingById: (id: string) => BenchmarkingItem | undefined;
  getMergedBenchmarking: () => BenchmarkingMerged[];
};

export const useBenchmarkingStore = create<BenchmarkingStore>((set, get) => ({
  commonProps: defaultCommonProps,
  items: [],

  setCommonProps: patch =>
    set(state => ({
      commonProps: { ...state.commonProps, ...patch },
    })),

  resetCommonProps: () => set({ commonProps: defaultCommonProps }),

  addBenchmarking: item => {
    if (get().items.length >= MAX_BENCHMARKING_ITEMS) return '';
    const benchmarking = createEmptyBenchmarking(item);
    set(state => ({
      items: [...state.items, benchmarking],
    }));
    return benchmarking.id;
  },

  updateBenchmarking: (id, patch) =>
    set(state => ({
      items: state.items.map(it => (it.id === id ? { ...it, ...patch } : it)),
    })),

  removeBenchmarking: id =>
    set(state => {
      const nextItems = state.items.filter(it => it.id !== id);
      return { items: nextItems };
    }),

  duplicateBenchmarking: id => {
    const { items } = get();
    if (items.length >= MAX_BENCHMARKING_ITEMS) return null;

    const original = items.find(it => it.id === id);
    if (!original) return null;

    const copy: BenchmarkingItem = {
      ...original,
      id: newId(),
    };

    set(state => ({
      items: [...state.items, copy],
      activeScenarioId: copy.id,
    }));

    return copy.id;
  },

  reorderBenchmarking: (fromId, toId) =>
    set(state => {
      if (fromId === toId) return state;

      const fromIndex = state.items.findIndex(it => it.id === fromId);
      const toIndex = state.items.findIndex(it => it.id === toId);

      if (fromIndex < 0 || toIndex < 0) return state;

      const items = [...state.items];
      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);

      return { items };
    }),

  setAllBenchmarking: items =>
    set(state => ({
      items,
    })),

  resetBenchmarking: () => set({ items: [] }),

  getBenchmarkingById: id => get().items.find(it => it.id === id),

  getMergedBenchmarking: () => {
    const { commonProps, items } = get();
    return items.map(it => ({
      id: it.id,
      ...commonProps,
      customerId: it.customerId,
      vendorId: it.vendorId,
      invoicingConcept: it.invoicingConcept,
      localCurrency: it.localCurrency,
    }));
  },
}));
